import type { TreatmentRecord, TreatmentRecordInput } from '../types/treatment';
import type { CreateTreatmentRecordDto } from '@/generated/schemas/createTreatmentRecordDto';
import type { UpdateTreatmentRecordDto } from '@/generated/schemas/updateTreatmentRecordDto';
import {
  treatmentRecordsControllerFindByCustomer,
  treatmentRecordsControllerFindLatestByCustomer,
  treatmentRecordsControllerCreate,
  treatmentRecordsControllerUpdate,
  treatmentRecordsControllerRemove,
  treatmentRecordsControllerFindByDateRange,
  treatmentRecordsControllerFindAll,
} from '@/generated/api/treatment-records/treatment-records';
import { reservationsControllerCompleteVisit } from '@/generated/api/reservations/reservations';
import logger from '../utils/logger';

/**
 * string | undefined をそのまま返す（ISO 8601文字列前提）
 */
function toISOStringOrUndefined(value: string | undefined): string | undefined {
  return value ?? undefined;
}

/**
 * 施術記録の管理を行うサービスクラス
 * BE API (Orval生成) を使用
 */
export class TreatmentService {
  /**
   * TreatmentRecordInput → CreateTreatmentRecordDto 変換
   */
  private static toCreateDto(
    customerId: string,
    record: TreatmentRecordInput
  ): CreateTreatmentRecordDto {
    return {
      customerId,
      customerName: record.customerName,
      treatmentDate: toISOStringOrUndefined(record.treatmentDate) ?? '',
      status: record.status as CreateTreatmentRecordDto['status'],
      staffId: record.staffId,
      staffName: record.staffName,
      reservationId: record.reservationId,
      actualStartTime: toISOStringOrUndefined(record.actualStartTime),
      actualEndTime: toISOStringOrUndefined(record.actualEndTime),
      physicalCondition:
        record.physicalCondition as CreateTreatmentRecordDto['physicalCondition'],
      symptoms: record.symptoms,
      painLevel: record.painLevel,
      stressLevel: record.stressLevel,
      sleepQuality: record.sleepQuality,
      medicalHistory: record.medicalHistory,
      allergies: record.allergies,
      medications: record.medications,
      treatmentMenu: record.treatmentMenu,
      treatmentDuration: record.treatmentDuration,
      techniques: record.techniques,
      focusAreas: record.focusAreas,
      pressure: record.pressure,
      lifestyle: record.lifestyle,
      requests: record.requests,
      concerns: record.concerns,
      notes: record.notes,
      improvements: record.improvements,
      feedback: record.feedback,
      nextRecommendation: record.nextRecommendation,
      nextFocusAreas: record.nextFocusAreas,
      followUpRequired: record.followUpRequired,
      products: record.products,
      treatmentAmount: record.treatmentAmount,
      productAmount: record.productAmount,
      paymentMethod:
        record.paymentMethod as CreateTreatmentRecordDto['paymentMethod'],
      pointsUsed: record.pointsUsed,
      pointsEarned: record.pointsEarned,
      isFirstVisit: record.isFirstVisit,
      tags: record.tags,
    };
  }

  /**
   * 指定顧客の全施術記録を取得
   * @throws API通信エラー時（呼び出し側でcatchすること）
   */
  static async getTreatmentRecords(
    customerId: string
  ): Promise<TreatmentRecord[]> {
    const data = await treatmentRecordsControllerFindByCustomer(customerId);
    return (data as unknown as TreatmentRecord[]) || [];
  }

  /**
   * 指定顧客の最新施術記録を取得
   * @throws API通信エラー時（呼び出し側でcatchすること）
   */
  static async getLatestTreatmentRecord(
    customerId: string
  ): Promise<TreatmentRecord | null> {
    const data =
      await treatmentRecordsControllerFindLatestByCustomer(customerId);
    return (data as unknown as TreatmentRecord) || null;
  }

  /**
   * 新規施術記録を保存
   */
  static async saveTreatmentRecord(
    customerId: string,
    record: TreatmentRecordInput
  ): Promise<TreatmentRecord> {
    try {
      const dto = this.toCreateDto(customerId, record);
      const data = await treatmentRecordsControllerCreate(dto);
      return data as unknown as TreatmentRecord;
    } catch (error) {
      logger.error('Failed to save treatment record:', error);
      throw new Error('施術記録の保存に失敗しました');
    }
  }

  /**
   * 既存施術記録を更新
   */
  static async updateTreatmentRecord(
    _customerId: string,
    id: string,
    updates: Partial<TreatmentRecord>
  ): Promise<TreatmentRecord | null> {
    try {
      // 明示的pickで非DTOフィールドを除外し、undefinedエントリも除去（部分更新対応）
      const raw: Record<string, unknown> = {
        customerName: updates.customerName,
        treatmentDate: toISOStringOrUndefined(updates.treatmentDate),
        status: updates.status,
        staffId: updates.staffId,
        staffName: updates.staffName,
        reservationId: updates.reservationId,
        actualStartTime: toISOStringOrUndefined(updates.actualStartTime),
        actualEndTime: toISOStringOrUndefined(updates.actualEndTime),
        physicalCondition: updates.physicalCondition,
        symptoms: updates.symptoms,
        painLevel: updates.painLevel,
        stressLevel: updates.stressLevel,
        sleepQuality: updates.sleepQuality,
        medicalHistory: updates.medicalHistory,
        allergies: updates.allergies,
        medications: updates.medications,
        treatmentMenu: updates.treatmentMenu,
        treatmentDuration: updates.treatmentDuration,
        techniques: updates.techniques,
        focusAreas: updates.focusAreas,
        pressure: updates.pressure,
        lifestyle: updates.lifestyle,
        requests: updates.requests,
        concerns: updates.concerns,
        notes: updates.notes,
        improvements: updates.improvements,
        feedback: updates.feedback,
        nextRecommendation: updates.nextRecommendation,
        nextFocusAreas: updates.nextFocusAreas,
        followUpRequired: updates.followUpRequired,
        products: updates.products,
        treatmentAmount: updates.treatmentAmount,
        productAmount: updates.productAmount,
        paymentMethod: updates.paymentMethod,
        pointsUsed: updates.pointsUsed,
        pointsEarned: updates.pointsEarned,
        isFirstVisit: updates.isFirstVisit,
        tags: updates.tags,
      };
      const updateDto = Object.fromEntries(
        Object.entries(raw).filter(([, v]) => v !== undefined)
      ) as UpdateTreatmentRecordDto;
      const data = await treatmentRecordsControllerUpdate(id, updateDto);
      return (data as unknown as TreatmentRecord) || null;
    } catch (error) {
      logger.error('Failed to update treatment record:', error);
      throw new Error('施術記録の更新に失敗しました');
    }
  }

  /**
   * 指定期間の施術記録を取得
   */
  static async getTreatmentRecordsByDateRange(
    customerId: string,
    startDate: Date,
    endDate: Date
  ): Promise<TreatmentRecord[]> {
    const data = await treatmentRecordsControllerFindByDateRange(customerId, {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
    return (data as unknown as TreatmentRecord[]) || [];
  }

  /**
   * 指定顧客の来店回数を取得
   * @throws API通信エラー時
   */
  static async getVisitCount(customerId: string): Promise<number> {
    const records = await this.getTreatmentRecords(customerId);
    return records.length;
  }

  /**
   * 施術記録を削除
   */
  static async deleteTreatmentRecord(
    _customerId: string,
    id: string
  ): Promise<boolean> {
    try {
      await treatmentRecordsControllerRemove(id);
      return true;
    } catch (error) {
      logger.error('Failed to delete treatment record:', error);
      throw new Error('施術記録の削除に失敗しました');
    }
  }

  /**
   * 全施術記録を取得（テナントスコープ）
   * @throws API通信エラー時（Promise.allで検知可能）
   */
  static async getAllTreatmentRecords(): Promise<TreatmentRecord[]> {
    const MAX_LIMIT = 10000;
    const data = await treatmentRecordsControllerFindAll({ limit: MAX_LIMIT });
    const result = data as unknown as {
      items: TreatmentRecord[];
      total: number;
    };
    if (result?.total > MAX_LIMIT) {
      logger.warn(
        `[TreatmentService] 施術記録が上限を超過: ${result.total}件中${MAX_LIMIT}件のみ取得`
      );
    }
    return result?.items || [];
  }

  /**
   * 予約を来店済みにする（施術記録化 + 完了遷移）。
   *
   * Issue #3800 (B-1): 旧 dormant 実装は FE で「record 作成 → PATCH status COMPLETED」の
   * 2 連打だった。遷移ルール上 CONFIRMED→COMPLETED が不許可のため後半が 400 になると
   * 「施術記録は作られたが予約は CONFIRMED のまま」の半端状態を構造的に残し、
   * さらに treatmentDate=now / treatmentAmount=0 という prefill 不備もあった。
   *
   * backend の complete-visit endpoint が 1 tx で record 作成 + COMPLETED 遷移 + 顧客統計
   * 更新を原子的に行い、treatmentDate（予約日時 JST）/ treatmentAmount（totalAmount）の
   * prefill も backend (B-4) が実施するため、FE はこの endpoint を呼ぶだけにする。
   *
   * staffId 未割当の予約は呼び出し側 UI で割当を促すが、endpoint 側も 400 で
   * loud-fail する（二重防御）。
   */
  static async completeVisitFromReservation(
    reservationId: string
  ): Promise<void> {
    try {
      await reservationsControllerCompleteVisit(reservationId);
    } catch (error) {
      logger.error('Failed to complete visit from reservation:', error);
      throw error instanceof Error
        ? error
        : new Error('来店済み処理に失敗しました');
    }
  }
}
